/**
 * phi-product-gallery - Hermès-style product gallery custom element
 * Encapsulates Swiper carousel with thumbnails, animated indicator, and ARIA tab pattern
 *
 * Usage:
 *   <phi-product-gallery section-id="{{ section.id }}">
 *     ... gallery HTML (swiper + thumbnails) ...
 *   </phi-product-gallery>
 *
 * API:
 *   - setActiveMedia(mediaId) - Switch to slide with given media ID (for variant changes)
 */
if (!customElements.get("phi-product-gallery")) {
  customElements.define(
    "phi-product-gallery",
    class PhiProductGallery extends HTMLElement {
      static get observedAttributes() {
        return ["section-id"];
      }

      constructor() {
        super();
        this.sectionId = this.getAttribute("section-id") || "";
        this.swiper = null;
        this.thumbTabs = [];
        this.slidePanels = [];
        this.animationIndicator = null;
        this.resizeObserver = null;
      }

      connectedCallback() {
        this.sectionId = this.getAttribute("section-id") || this.sectionId;
        this.initWhenReady();
      }

      disconnectedCallback() {
        this.destroy();
      }

      attributeChangedCallback(name, oldVal, newVal) {
        if (name === "section-id" && oldVal !== newVal) {
          this.sectionId = newVal || "";
        }
      }

      initWhenReady() {
        const start = Date.now();
        const maxWait = 5000;
        const tryInit = () => {
          if (typeof Swiper === "undefined") {
            if (Date.now() - start < maxWait) {
              requestAnimationFrame(tryInit);
            }
            return;
          }
          requestAnimationFrame(() => this.init());
        };
        tryInit();
      }

      init() {
        const swiperEl = this.querySelector(".phi-p--main-swiper, .swiper");
        const thumbWrapper = this.querySelector(".phi-p--thumbnails-wrapper");
        const thumbItems = this.querySelectorAll(".phi-p--thumb-item");
        const slides = this.querySelectorAll(
          ".swiper-slide:not(.phi-p--spacer-slide)",
        );

        if (!swiperEl || thumbItems.length === 0 || slides.length === 0) return;

        // Prevent Swiper from intercepting events on the thumbnails wrapper
        // Swiper uses Pointer Events API with setPointerCapture which can
        // redirect pointer events away from thumbnails to the swiper element
        if (thumbWrapper) {
          ["mousedown", "touchstart", "pointerdown"].forEach((evt) => {
            thumbWrapper.addEventListener(evt, (e) => e.stopPropagation(), {
              passive: true,
            });
          });
          // Release any pointer capture that Swiper might set on thumbnails
          thumbWrapper.addEventListener("gotpointercapture", (e) => {
            e.target.releasePointerCapture(e.pointerId);
          });
        }
        // Also isolate individual thumbnail items from Swiper pointer capture
        thumbItems.forEach((thumb) => {
          thumb.addEventListener("gotpointercapture", (e) => {
            e.target.releasePointerCapture(e.pointerId);
          });
          thumb.addEventListener("pointerdown", (e) => e.stopPropagation());
        });

        // Add animated indicator (Hermès-style)
        this.addAnimationIndicator(thumbWrapper, thumbItems);

        // Add ARIA tab pattern
        this.setupTabPattern(thumbWrapper, thumbItems, slides);

        // Initialize Swiper
        this.initSwiper(swiperEl, thumbItems, slides.length);

        // Store reference for variant updates
        if (this.sectionId) {
          window[`phiProductGallery_${this.sectionId}`] = this;
        }
      }

      addAnimationIndicator(thumbWrapper, thumbItems) {
        if (!thumbWrapper || thumbItems.length === 0) return;

        this.animationIndicator = document.createElement("div");
        this.animationIndicator.className = "phi-p--animation-indicator";
        this.animationIndicator.setAttribute("aria-hidden", "true");
        thumbWrapper.appendChild(this.animationIndicator);

        // Size and position based on first thumb
        const firstThumb = thumbItems[0];
        if (firstThumb) {
          const rect = firstThumb.getBoundingClientRect();
          this.animationIndicator.style.width = `${rect.width}px`;
          this.animationIndicator.style.height = "2px";
          this.updateIndicatorPosition(0, thumbItems);
        }
      }

      updateIndicatorPosition(activeIndex, thumbItems) {
        if (!this.animationIndicator || !thumbItems.length) return;

        const activeThumb = thumbItems[activeIndex];
        if (!activeThumb) return;

        const wrapper = this.animationIndicator.parentElement;
        if (!wrapper) return;

        const wrapperRect = wrapper.getBoundingClientRect();
        const thumbRect = activeThumb.getBoundingClientRect();

        // Calculate horizontal offset from wrapper's left edge
        // Account for wrapper's padding (4px)
        const wrapperPaddingLeft =
          parseInt(getComputedStyle(wrapper).paddingLeft) || 4;
        const offsetX = thumbRect.left - wrapperRect.left - wrapperPaddingLeft;

        // Set width to match thumbnail width
        this.animationIndicator.style.width = `${thumbRect.width}px`;

        // Only translate horizontally (bottom: 0 handles vertical position)
        this.animationIndicator.style.transform = `translate3d(${offsetX}px, 0, 0)`;
        this.animationIndicator.style.transition =
          "transform 0.35s ease-out, width 0.35s ease-out";
      }

      setupTabPattern(thumbWrapper, thumbItems, slides) {
        const galleryId = `gallery-${this.sectionId}`;
        thumbWrapper?.setAttribute("role", "tablist");
        thumbWrapper?.setAttribute("aria-labelledby", `${galleryId}-label`);

        // Add sr-only label if not present
        let label = this.querySelector(`#${galleryId}-label`);
        if (!label) {
          label = document.createElement("span");
          label.id = `${galleryId}-label`;
          label.className = "visually-hidden";
          label.textContent = "Image gallery";
          thumbWrapper?.insertBefore(label, thumbWrapper.firstChild);
        }

        thumbItems.forEach((thumb, index) => {
          const tabId = `gallery-tab-${this.sectionId}-${index}`;
          const panelId = `gallery-panel-${this.sectionId}-${index}`;

          thumb.setAttribute("role", "tab");
          thumb.setAttribute("id", tabId);
          thumb.setAttribute("aria-selected", index === 0 ? "true" : "false");
          thumb.setAttribute("aria-controls", panelId);
          thumb.setAttribute("tabindex", index === 0 ? "0" : "-1");
          thumb.removeAttribute("aria-pressed");
        });

        slides.forEach((slide, index) => {
          const panelId = `gallery-panel-${this.sectionId}-${index}`;
          const tabId = `gallery-tab-${this.sectionId}-${index}`;
          const total = slides.length;

          slide.setAttribute("role", "tabpanel");
          slide.setAttribute("id", panelId);
          slide.setAttribute("aria-labelledby", tabId);
          slide.setAttribute("aria-label", `${index + 1} / ${total}`);
        });
      }

      initSwiper(swiperEl, thumbItems, slideCount) {
        const prevBtn = this.querySelector(".phi-p--nav-prev");
        const nextBtn = this.querySelector(".phi-p--nav-next");
        const paginationEl = this.querySelector(".phi-p--pagination");
        const isMobile = () => window.innerWidth < 750;

        const updateUI = (activeIndex) => {
          thumbItems.forEach((thumb, index) => {
            const isActive = index === activeIndex;
            thumb.classList.toggle("is-active", isActive);
            thumb.setAttribute("aria-selected", isActive);
            thumb.setAttribute("tabindex", isActive ? "0" : "-1");
          });

          this.updateIndicatorPosition(activeIndex, thumbItems);

          if (prevBtn) {
            prevBtn.classList.toggle(
              "swiper-button-disabled",
              activeIndex <= 0,
            );
          }
          if (nextBtn) {
            nextBtn.classList.toggle(
              "swiper-button-disabled",
              activeIndex >= slideCount - 1,
            );
          }
        };

        this.swiper = new Swiper(swiperEl, {
          slidesPerView: isMobile() ? 1 : "auto",
          spaceBetween: isMobile() ? 0 : 0,
          loop: false,
          speed: 500,
          resistanceRatio: 0,
          mousewheel: {
            // Enables mousewheel/touchpad control
            enabled: true,
            // Optional: Forces the swiper to only react to the relevant axis of scrolling (horizontal/vertical)
            forceToAxis: true,
            // Optional: Allows normal page scrolling when the swiper is at its beginning or end
            releaseOnEdges: true,
          },
          // Other Swiper parameters (pagination, navigation, etc.)
          grabCursor: true, // Shows a "grab" cursor on hover
          keyboard: { enabled: true, onlyInViewport: true },
          pagination: paginationEl
            ? {
                el: paginationEl,
                clickable: true,
                dynamicBullets: true,
                dynamicMainBullets: 1,
                renderBullet: (index, className) =>
                  index < slideCount
                    ? `<span class="${className}" aria-label="Go to slide ${index + 1}"></span>`
                    : "",
              }
            : false,
          on: {
            slideChange: function () {
              swiperEl.querySelectorAll("video").forEach((v) => v.pause());
              updateUI(this.activeIndex);
            },
            init: function () {
              updateUI(0);
            },
          },
        });

        prevBtn?.addEventListener("click", () => this.swiper?.slidePrev());
        nextBtn?.addEventListener("click", () => this.swiper?.slideNext());

        thumbItems.forEach((thumb) => {
          const handleActivate = (e) => {
            if (e) {
              e.stopPropagation();
              e.preventDefault();
            }
            const index = parseInt(thumb.dataset.thumbIndex, 10);
            if (isNaN(index)) return;
            this.swiper?.slideTo(index, 500);
            // Force-update UI even if slideChange doesn't fire
            // (happens with centeredSlidesBounds at first/last slide boundaries)
            updateUI(index);
          };
          thumb.addEventListener("click", handleActivate);
          thumb.addEventListener("keydown", (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleActivate(e);
            }
          });
        });

        // Arrow key navigation between tabs
        thumbItems.forEach((thumb, index) => {
          thumb.addEventListener("keydown", (e) => {
            if (e.key === "ArrowRight" || e.key === "ArrowDown") {
              e.preventDefault();
              const next = thumbItems[index + 1];
              next?.focus();
            } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
              e.preventDefault();
              const prev = thumbItems[index - 1];
              prev?.focus();
            }
          });
        });

        // Resize handler
        let resizeTimer;
        window.addEventListener("resize", () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => {
            const mobile = isMobile();
            this.swiper.params.slidesPerView = mobile ? 1 : "auto";
            this.swiper.update();
            this.updateIndicatorPosition(this.swiper.activeIndex, thumbItems);
          }, 250);
        });
      }

      /**
       * Switch to slide by media ID (for variant changes)
       * @param {string|number} mediaId - The media ID to show
       */
      setActiveMedia(mediaId) {
        if (!this.swiper || !mediaId) return;

        const slides = this.querySelectorAll(
          ".swiper-slide:not(.phi-p--spacer-slide)",
        );
        for (let i = 0; i < slides.length; i++) {
          console.log(slides[i].dataset.mediaId, mediaId);
          if (slides[i].dataset.mediaId == mediaId) {
            this.swiper.slideTo(i, 500);
            return;
          }
        }
      }

      destroy() {
        this.swiper?.destroy(true, true);
        this.swiper = null;
        if (this.sectionId && window[`phiProductGallery_${this.sectionId}`]) {
          delete window[`phiProductGallery_${this.sectionId}`];
        }
      }
    },
  );
}
